import { useMsal } from '@azure/msal-react'
import { createContext, useContext, useEffect, useState } from 'react'
import { fetchPendingApprovalCount } from '../data/advertisement.service'
import { useSessionInfo } from './SessionContext'

interface PendingApprovalContextType {
  pendingCount: number
  isLoading: boolean
  refreshCount: () => Promise<void>
}

const PendingApprovalContext = createContext<
  PendingApprovalContextType | undefined
>(undefined)

export const usePendingApproval = () => {
  const context = useContext(PendingApprovalContext)
  if (!context) {
    throw new Error(
      'usePendingApproval must be used within a PendingApprovalProvider',
    )
  }
  return context
}

interface PendingApprovalProviderProps {
  children: React.ReactNode
}

export const PendingApprovalProvider = ({
  children,
}: PendingApprovalProviderProps) => {
  const [pendingCount, setPendingCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const { session } = useSessionInfo()
  const { instance } = useMsal()

  const fetchCount = async () => {
    if (!session?.organizationId || !instance) return

    setIsLoading(true)
    try {
      const count = await fetchPendingApprovalCount(
        session.organizationId,
        instance,
      )
      setPendingCount(count)
    } catch (error) {
      console.error('Error fetching pending approval count:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const refreshCount = async () => {
    await fetchCount()
  }

  useEffect(() => {
    fetchCount()
  }, [session?.organizationId, instance])

  return (
    <PendingApprovalContext.Provider
      value={{
        pendingCount,
        isLoading,
        refreshCount,
      }}
    >
      {children}
    </PendingApprovalContext.Provider>
  )
}
